import { Button } from "components/LEGACY/styled-components/Buttons"
import Input from "components/utils/InputField"
import { functions } from "firebase/firebase"
import { deleteDoc } from "firebase/firestore"
import { WithRef } from "hooks/firestore/FirestoreDocument"
import { getSubscriberRef } from "hooks/firestore/getRefs"
import { useShop } from "hooks/firestore/simple/useShop"
import { useSubscribers } from "hooks/firestore/simple/useSubscribers"
import { useDisplayToast } from "hooks/misc/useDisplayToast"
import { useMemo, useState } from "react"
import { Loader, Save, SearchAlt } from "styled-icons/boxicons-regular"
import { Close } from "styled-icons/evil"
import { Add } from "styled-icons/ionicons-sharp"
import { Subscriber } from "types/firestore/subscriber"
import validator from "validator"

export default function NewsletterSubscribers() {
  const allSubscribers = useSubscribers()
  const [searchTerm, setSearchTerm] = useState("")

  const filteredSubscribers = useMemo(() => {
    if (!allSubscribers) return []

    // Filter based on search
    const filtered = searchTerm
      ? allSubscribers.filter(sub => sub.email.toLowerCase().includes(searchTerm.toLowerCase()))
      : allSubscribers

    // Sort alphabetically by email
    return [...filtered].sort((a, b) => a.email.localeCompare(b.email))
  }, [allSubscribers, searchTerm])

  return (
    <div>
      <div className="tw-mb-4 tw-flex tw-items-center tw-gap-2 tw-max-w-3xl">
        <Input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="tw-flex-1">
          Suche nach E-Mail
        </Input>
        <Button className="tw-w-fit">
          <SearchAlt size="22" color="currentColor" />
        </Button>
      </div>

      <ul className="tw-list-none tw-space-y-3 tw-pl-0 tw-max-w-3xl">
        {filteredSubscribers.map(subscriber => (
          <SubscriberCard key={subscriber._ref.id} subscriber={subscriber} />
        ))}
        {<AddNewSubscriber />}
      </ul>
    </div>
  )
}

function SubscriberCard({ subscriber }: { subscriber: WithRef<Subscriber> }) {
  const shop = useShop()
  const displayToast = useDisplayToast()
  const [isResending, setIsResending] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  const resendOptInEmail = async () => {
    if (!shop?._ref) return displayToast("Ein Fehler ist aufgetreten", "error")
    try {
      setIsResending(true)
      await functions.httpsCallable("groupNL-signUpEmailToNewsletter")({
        email: subscriber.email,
        shopId: shop._ref.id,
        version: "0.1",
      })

      displayToast("Die Opt-In E-Mail wurde erneut gesendet.", "success")
    } catch (e) {
      displayToast("Fehler beim Senden der Opt-In E-Mail.", "error")
    } finally {
      setIsResending(false)
    }
  }

  const handleDelete = async () => {
    if (!shop?._ref) return alert("Ein Fehler ist aufgetreten")

    setIsDeleting(true)
    const ref = getSubscriberRef(shop._ref.id, subscriber._ref.id)
    if (!ref) return displayToast("Dieser Abonnent wurde nicht gefunden", "error")

    try {
      await deleteDoc(ref)
      displayToast("Der Abonnent wurde erfolgreich gelöscht", "success")
    } catch (e) {
      displayToast("Fehler beim Löschen", "error")
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <li className="tw-rounded-lg tw-bg-white tw-p-4 tw-text-base">
      <div className="tw-flex tw-items-center tw-justify-between">
        <span className="tw-inline-block tw-align-middle tw-text-xl tw-font-bold tw-text-green-primary">
          {subscriber.email}
        </span>

        <div className="tw-flex tw-items-center tw-gap-x-2">
          <span className="tw-rounded-full tw-bg-gray-200 tw-px-2 tw-py-1 tw-text-sm">
            {getLabelForSubscriberStatus(subscriber.status)}
          </span>

          {subscriber.status === "requestedOptIn" && (
            <button
              className="tw-w-fit tw-bg-green-primary/10 tw-text-green-primary tw-rounded-lg tw-px-2 tw-py-1 tw-text-sm"
              onClick={resendOptInEmail}
              disabled={isResending}
            >
              {isResending ? <Loader size="18" color="currentColor" /> : "E-Mail erneut senden"}
            </button>
          )}

          <button
            className="tw-w-fit tw-bg-red-50 tw-text-red-700 tw-rounded-lg tw-px-2 tw-py-1 tw-text-sm"
            onClick={handleDelete}
            disabled={isDeleting}
          >
            {isDeleting ? <Loader size="18" color="currentColor" /> : "Löschen"}
          </button>
        </div>
      </div>
    </li>
  )
}

function AddNewSubscriber() {
  const [adding, setAdding] = useState(false)
  const [loading, setLoading] = useState(false)
  const [mail, setMail] = useState("")

  const shop = useShop()

  const displayToast = useDisplayToast()

  const addNewSubscriber = async () => {
    if (!shop?._ref) return alert("Ein Fehler ist aufgetreten")
    try {
      setLoading(true)
      await functions.httpsCallable("groupNL-signUpEmailToNewsletter")({
        email: mail,
        shopId: shop._ref.id,
        version: "0.1",
      })

      displayToast("Der Nutzer hat eine E-Mail erhalten, um seine Newsletter-Abonnement zu bestätigen.", "success")
    } catch (e) {
      displayToast("Fehler beim Anlegen des Newsletter-Abonnements.", "error")
    } finally {
      setMail("")
      setAdding(false)
      setLoading(false)
    }
  }

  if (!adding) {
    return (
      <button
        onClick={() => setAdding(true)}
        className="tw-flex tw-w-full tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-dashed tw-border-gray-500 tw-p-4 tw-text-base hover:tw-border-solid"
      >
        <Add className="tw-mr-2 tw-size-4" /> Neuer Newsletter-Abonnement
      </button>
    )
  }

  return (
    <div className="tw-flex tw-flex-col tw-w-full tw-gap-2">
      <Input {...{ value: mail, onChange: v => setMail(v.target.value) }} className="tw-flex-1">
        E-Mail
      </Input>

      <div className="tw-flex tw-items-center tw-gap-2">
        <Button className="tw-w-fit" onClick={addNewSubscriber} disabled={!validator.isEmail(mail)}>
          {loading ? <Loader size="22" color="currentColor" /> : <Save size="22" color="currentColor" />}
        </Button>
        <Button className="tw-w-fit" onClick={() => setAdding(false)}>
          <Close size="22" color="currentColor" />
        </Button>
      </div>
    </div>
  )
}

function getLabelForSubscriberStatus(status: Subscriber["status"]) {
  switch (status) {
    case "subscribed":
      return "Abonniert"
    case "unsubscribed":
      return "Nicht mehr abonniert"
    case "requestedOptIn":
      return "Abonemment angefragt"
    case "pending":
      return "Warten"
    default:
      return "Unbekannt"
  }
}
